// God → Notifications: every raw bank notification devices sent, with filters, parse
// results, per-app capture coverage and silent-device detection.
import { useCallback, useEffect, useState } from "react";
import { Activity, Bell, BellOff, Copy, Filter, Layers, ListChecks, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { god } from "../godApi";
import { useClientPage } from "../../components/sc/Pager";
import {
  Button, Callout, Cell, Code, Empty, Field, Grid, Health, Input, Kpi, Page, Pagination, Panel, PanelHero, Pill, Rows,
  Section, Segmented, Select, Table, Toolbar, ago, inr, shortId, when,
} from "../kit";

const REASON = {
  matched: ["green", "Settled"], no_match: ["amber", "Credit · no match"], unparsed: ["gray", "Not a credit"],
  dup_content: ["gray", "Duplicate"], ingest_error: ["red", "Ingest error"],
};
const EMPTY_F = { merchant_id: "", device_id: "", package_name: "", reason: "", matched: "", q: "", since: "", until: "" };
const GAP_HOURS = [[1, "1 hour"], [6, "6 hours"], [24, "24 hours"], [72, "3 days"], [168, "7 days"]];
const n0 = (v) => Number(v || 0).toLocaleString("en-IN");

function Reason({ r }) {
  const [tone, label] = REASON[r] || ["gray", r || "—"];
  return <Pill tone={tone} dot>{label}</Pill>;
}

export default function Notifications() {
  const [facets, setFacets] = useState({ merchants: [], devices: [], apps: [] });
  const [f, setFilters] = useState(EMPTY_F);
  const [qInput, setQInput] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [gapHours, setGapHours] = useState(24);
  const [gapErr, setGapErr] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const skip = (page - 1) * size;
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(null);
  const [view, setView] = useState("feed");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [d, s, c] = await Promise.all([
        god.notifications({ ...f, limit: size, skip }),
        god.notificationStats(f),
        god.notificationCoverage(f),
      ]);
      setData(d); setStats(s); setCoverage(c);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); setLoaded(true); }
  }, [f, skip, size]);

  // Gap-detection sweep (registered but silent devices) — global, not tied to the filters.
  useEffect(() => {
    god.deviceGaps(gapHours).then((g) => { setGaps(g); setGapErr(""); }).catch((e) => setGapErr(e.message));
  }, [gapHours]);
  useEffect(() => { god.notificationFacets().then(setFacets).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const setF = (patch) => { setPage(1); setFilters((p) => ({ ...p, ...patch })); };
  const clear = () => { setQInput(""); setPage(1); setFilters(EMPTY_F); };
  const devs = facets.devices.filter((d) => !f.merchant_id || d.merchant_id === f.merchant_id);
  const filtered = Object.values(f).some(Boolean);

  const covApps = (coverage?.apps || []).map((a) => ({ ...a, id: a.package_name }));
  const silent = (gaps?.silent_devices || []).map((g) => ({ ...g, id: g.device_id }));

  return (
    <Page>
      <Section title="Filters" sub={filtered ? "Filters apply to the stats, feed and coverage." : "Showing everything."} icon={Filter}>
        <div className="nt-filters">
          <Field label="Merchant">
            <Select value={f.merchant_id} onChange={(e) => setF({ merchant_id: e.target.value, device_id: "" })}
              options={[["", "All merchants"], ...facets.merchants.map((m) => [m.id, m.name || m.id])]} />
          </Field>
          <Field label="Device">
            <Select value={f.device_id} onChange={(e) => setF({ device_id: e.target.value })}
              options={[["", "All devices"], ...devs.map((d) => [d.id, d.name])]} />
          </Field>
          <Field label="App">
            <Select value={f.package_name} onChange={(e) => setF({ package_name: e.target.value })}
              options={[["", "All apps"], ...facets.apps.map((a) => [a.package_name, `${a.label || a.package_name} · ${a.count}`])]} />
          </Field>
          <Field label="Result">
            <Select value={f.reason} onChange={(e) => setF({ reason: e.target.value })}
              options={[["", "All results"], ["matched", "Settled"], ["no_match", "Credit · no match"], ["unparsed", "Not a credit"], ["dup_content", "Duplicate"]]} />
          </Field>
          <div className="nt-wide">
            <Field label="Search" hint="Title, body, app or bank. Press Enter to apply.">
              <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Text or app…"
                onKeyDown={(e) => { if (e.key === "Enter") setF({ q: qInput }); }} />
            </Field>
          </div>
          <Field label="From">
            <Input type="date" value={(f.since || "").slice(0, 10)} onChange={(e) => setF({ since: e.target.value ? e.target.value + "T00:00:00+00:00" : "" })} />
          </Field>
          <Field label="To">
            <Input type="date" value={(f.until || "").slice(0, 10)} onChange={(e) => setF({ until: e.target.value ? e.target.value + "T23:59:59+00:00" : "" })} />
          </Field>
        </div>
        <div className="k-actions" style={{ marginTop: 14 }}>
          <Button onClick={clear} disabled={!filtered && !qInput}>Clear</Button>
          <Button variant="primary" onClick={() => setF({ q: qInput })} disabled={busy}>{busy ? "Loading…" : "Apply"}</Button>
        </div>
      </Section>

      <Grid cols={3}>
        <Kpi hero icon={Bell} label="Notifications" value={stats ? n0(stats.total) : "—"} sub="matching the filters" />
        <Kpi icon={Activity} label="Credits parsed" value={stats ? n0(stats.credits) : "—"} sub="money-in alerts" />
        <Kpi icon={ListChecks} label="Auto-settled" value={stats ? n0(stats.matched) : "—"} sub={stats && stats.credits ? `${Math.round((stats.matched / stats.credits) * 100)}% of credits` : "matched to a payment"} />
        <Kpi icon={Layers} label="Not a credit" value={stats ? n0(stats.unparsed) : "—"} sub="ignored by the parser" />
        <Kpi icon={Copy} label="Duplicates" value={stats ? n0(stats.dup) : "—"} sub="same content seen twice" />
        <Kpi icon={BellOff} label={`Silent devices (${gapHours}h)`} value={gaps ? n0(gaps.count) : "—"} sub={gaps?.count ? "no captured credit" : "all devices capturing"} onClick={() => setView("gaps")} />
      </Grid>

      <Segmented value={view} onChange={setView} testIdPrefix="god-notif-view" options={[
        { key: "feed", label: "Feed", count: data.total },
        { key: "coverage", label: "Coverage", count: covApps.length },
        { key: "gaps", label: "Device gaps", count: gaps?.count },
      ]} />

      {view === "feed" && (
        <>
          <div className="k-split">
            <Section title="Daily volume" sub="Last 30 days" icon={Activity}
              actions={<span className="nt-legend"><span><i style={{ background: "#2f33d6" }} />Settled</span><span><i style={{ background: "#C9CCF7" }} />Total</span></span>}>
              <DailyBars rows={(stats?.timeseries || []).slice(-30)} />
            </Section>
            <Section title="Top apps" icon={Smartphone}>
              {(stats?.by_app || []).length === 0 ? <div className="k-faint" style={{ fontSize: 13 }}>No data yet.</div> : (
                <Rows items={(stats.by_app || []).slice(0, 8).map((a) => [a.label || a.package_name, n0(a.count)])} />
              )}
            </Section>
          </div>

          <Section title="Notifications" sub="Newest first. Click one for the full text." icon={Bell} pad={false}>
            <Table testId="god-notifs" rows={data.items} loading={busy && !loaded} onRowClick={setOpen} activeId={open?.id}
              empty={<Empty icon={Bell} title="No notifications match">The log fills as devices send notifications from when this feature went live.</Empty>}
              columns={[
                { key: "at", label: "Received", width: 130, render: (n) => <Cell main={when(n.created_at)} sub={ago(n.created_at)} /> },
                { key: "text", label: "Notification", render: (n) => <Cell main={n.bank_label || n.package_name} sub={<span className="nt-clip">{n.title || n.body || "(no text)"}</span>} /> },
                { key: "merchant", label: "Merchant", hideSm: true, render: (n) => <Cell main={n.merchant_name || null} sub={n.device_name || null} /> },
                { key: "amount", label: "Amount", align: "right", render: (n) => (n.amount != null ? <b>{inr(n.amount)}</b> : <span className="k-faint">—</span>) },
                { key: "reason", label: "Result", render: (n) => <Reason r={n.reason} /> },
              ]} />
            <Pagination page={page} pages={Math.max(1, Math.ceil((data.total || 0) / size))} total={data.total} size={size}
              sizes={[25, 50, 100, 200]} onPage={setPage} onSize={(s) => { setSize(s); setPage(1); }} />
          </Section>
        </>
      )}

      {view === "coverage" && <Coverage apps={covApps} />}

      {view === "gaps" && (
        <Section title="Silent devices" sub="Paired phones with no captured credit in the window. Catches a dead listener before the merchant notices." icon={BellOff} pad={false}>
          <Toolbar>
            <span className="nt-tl">No credit for</span>
            <Select value={gapHours} onChange={(e) => setGapHours(Number(e.target.value))} options={GAP_HOURS} aria-label="Silence window" />
          </Toolbar>
          {gapErr && <div className="nt-pad"><Callout tone="red" title="Couldn't run the gap check">{gapErr}</Callout></div>}
          <Table testId="god-device-gaps" rows={silent} loading={!gaps && !gapErr}
            empty={<Empty icon={Smartphone} title="No silent devices">Every paired phone captured a credit in this window.</Empty>}
            columns={[
              { key: "device", label: "Device", render: (g) => <Cell main={g.device_name} sub={g.app_version ? `v${g.app_version}` : null} /> },
              { key: "merchant", label: "Merchant", render: (g) => <Cell main={g.merchant_name || shortId(g.merchant_id)} /> },
              { key: "capture", label: "Last credit", render: (g) => (g.last_capture_at ? <Cell main={ago(g.last_capture_at)} sub={when(g.last_capture_at)} /> : <span className="k-faint">Never</span>) },
              { key: "seen", label: "Last seen", hideSm: true, render: (g) => (g.last_seen_at ? <Cell main={ago(g.last_seen_at)} sub={when(g.last_seen_at)} /> : <span className="k-faint">Never</span>) },
              { key: "access", label: "Notification access", render: (g) => (g.notif_access_granted == null ? <Pill>Unknown</Pill> : <Health ok={g.notif_access_granted} label={g.notif_access_granted ? "Granted" : "Revoked"} />) },
            ]} />
        </Section>
      )}

      {open && <NotifPanel n={open} onClose={() => setOpen(null)} />}

      <style>{`
        .nt-filters { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
        .nt-wide { grid-column: span 2; min-width: 0; display: flex; flex-direction: column; }
        .nt-clip { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; max-width: 520px; }
        .nt-legend { display: inline-flex; gap: 12px; font-size: 12.5px; color: #5b6170; }
        .nt-legend span { display: inline-flex; align-items: center; gap: 6px; }
        .nt-legend i { width: 9px; height: 9px; border-radius: 3px; }
        .nt-bars { display: flex; align-items: flex-end; gap: 4px; height: 160px; padding-top: 6px; }
        .nt-col { flex: 1; min-width: 0; height: 100%; display: flex; align-items: flex-end; }
        .nt-bar { width: 100%; background: #C9CCF7; border-radius: 4px 4px 2px 2px; display: flex; align-items: flex-end; overflow: hidden; }
        .nt-bar i { display: block; width: 100%; background: #2f33d6; }
        .nt-col:hover .nt-bar { background: #B4B8F2; }
        .nt-axis { display: flex; justify-content: space-between; margin-top: 8px; font-size: 11.5px; color: #8A90A1; font-variant-numeric: tabular-nums; }
        .nt-msg-title { font-weight: 700; font-size: 14px; color: #111827; overflow-wrap: anywhere; }
        .nt-msg-body { margin-top: 6px; font-size: 13.5px; line-height: 1.5; color: #374151; white-space: pre-wrap; overflow-wrap: anywhere; }
        .nt-tl { font-size: 13px; font-weight: 600; color: #374151; }
        .nt-pad { padding: 12px 18px 0; }
        @media (max-width: 1000px) { .nt-filters { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 560px) { .nt-filters { grid-template-columns: minmax(0, 1fr); } .nt-wide { grid-column: auto; } .nt-bars { gap: 2px; } }
      `}</style>
    </Page>
  );
}

function DailyBars({ rows }) {
  if (!rows.length) return <div className="k-faint" style={{ fontSize: 13 }}>No data yet.</div>;
  const max = Math.max(1, ...rows.map((t) => t.count));
  return (
    <>
      <div className="nt-bars" role="img" aria-label="Daily notifications, last 30 days">
        {rows.map((t) => (
          <div key={t.day} className="nt-col" title={`${t.day}: ${t.count} (${t.matched} settled)`}>
            <div className="nt-bar" style={{ height: `${Math.max(3, (t.count / max) * 100)}%` }}>
              <i style={{ height: `${t.count ? (t.matched / t.count) * 100 : 0}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="nt-axis"><span>{rows[0].day}</span><span>peak {n0(max)}/day</span><span>{rows[rows.length - 1].day}</span></div>
    </>
  );
}

function Coverage({ apps }) {
  const { slice, pager } = useClientPage(apps, 25);
  return (
    <Section title="Per-app capture coverage" sub="Which layer recovered the amount for each credit (Android 15/16 readiness)." icon={Layers} pad={false}>
      <Table testId="god-coverage" rows={slice}
        empty={<Empty icon={Layers} title="No credits yet">Coverage appears once devices capture credit alerts.</Empty>}
        columns={[
          { key: "app", label: "App", render: (a) => <Cell main={a.label || a.package_name} sub={a.label ? a.package_name : null} /> },
          { key: "credits", label: "Credits", align: "right", render: (a) => n0(a.credits) },
          { key: "settled", label: "Settled", align: "right", render: (a) => <b style={{ color: "#2f33d6" }}>{n0(a.matched)}</b> },
          { key: "text", label: "Text", align: "right", render: (a) => <span className={a.via_text ? "" : "k-faint"}>{n0(a.via_text)}</span> },
          { key: "a11y", label: "A11y", align: "right", render: (a) => <span className={a.via_a11y ? "" : "k-faint"}>{n0(a.via_a11y)}</span> },
          { key: "ocr", label: "OCR", align: "right", render: (a) => <span className={a.via_ocr ? "" : "k-faint"}>{n0(a.via_ocr)}</span> },
        ]} />
      <Pagination {...pager} pages={Math.max(1, Math.ceil(pager.total / pager.size))} sizes={[25, 50, 100]} />
    </Section>
  );
}

function NotifPanel({ n, onClose }) {
  return (
    <Panel title="Notification" sub={n.merchant_name || shortId(n.merchant_id)} onClose={onClose}>
      <PanelHero kicker={n.bank_label || n.package_name} title={n.amount != null ? inr(n.amount) : "No amount"}
        meta={<><Reason r={n.reason} /><span>{when(n.created_at, true)} · {ago(n.created_at)}</span></>} reference={n.id} />

      <Section title="Message">
        <div className="nt-msg-title">{n.title || "(no title)"}</div>
        <div className="nt-msg-body">{n.body || "(no body)"}</div>
      </Section>

      <Section title="Details">
        <Rows items={[
          ["Merchant", n.merchant_name || shortId(n.merchant_id)],
          ["Device", n.device_name || shortId(n.device_id)],
          ["App", n.package_name, { mono: true, copy: true }],
          ["Received", when(n.created_at, true)],
          ["Captured on phone", n.captured_at ? when(n.captured_at, true) : null],
          ["Matched a payment", n.matched == null ? null : n.matched ? "Yes" : "No"],
          ["Payment intent", n.intent_id, { mono: true, copy: true }],
        ]} />
      </Section>

      <Section title="Capture">
        <Rows items={[
          ["Source", n.source || "listener"],
          ["Custom view", n.has_custom_view ? "Yes" : "No"],
          ["Amount read via", n.amount_source],
          ["Image source", n.image_source],
          ["OCR confidence", n.ocr_confidence != null ? `${Math.round(n.ocr_confidence * 100)}%` : null],
          ["Extras keys", Array.isArray(n.extras_keys) && n.extras_keys.length ? n.extras_keys.join(", ") : null, { mono: true }],
        ]} />
      </Section>

      <Section title="Raw record">
        <Code value={n} />
      </Section>
    </Panel>
  );
}
